<i18n src="@/locales/components/CommentTranslate.json"></i18n>

<template>
	<div class="cbs-comment-general">
		<div
			v-if="comments.length"
			class="pt-4 pb-0"
		>
			<ul
				class="list-item comments__list-item border border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-6"
			>
				<li v-if="comments.length && commentData.has_more_pages" class="text-center pb-4">
					<vs-button class="font-bold list-item__btn-loadmore" @click.stop="loadMoreComments">{{ $t(`${displayMode === "post-comments" ? 'LoadMorePostComments': 'LoadMoreComments'}`) }}</vs-button>
				</li>
				<li
					:class="[{'flex-row-reverse': $store.state.AppActiveUser.role.id === item.author.id && displayMode === 'chat'}, 'mb-4 flex comment list-item__comment']"
					v-for="(item, index) in comments"
					:key="index"
				>
					<div :class="[{'mr-3': $store.state.AppActiveUser.role.id !== item.author.id || displayMode === 'post-comments', 'ml-3': $store.state.AppActiveUser.role.id === item.author.id && displayMode === 'chat'}]">
						<vs-avatar
							v-if="item.author && item.author.avatar"
							:class="['comment__avatar', {
								'ml-0': $store.state.AppActiveUser.role.id !== item.author.id || displayMode === 'post-comments',
								'mr-0': $store.state.AppActiveUser.role.id === item.author.id && displayMode === 'chat'
							}]"
							size="large"
							:src="item.author.avatar"
						/>
						<vs-avatar v-else class="comment__avatar" size="large" />
					</div>
					<div :class="[{'items-end': item.author && (item.author.id === $store.state.AppActiveUser.role.id) && displayMode === 'chat', 'items-start': item.author.id !== $store.state.AppActiveUser.role.id}, 'text-black flex flex-col']">
						<div :class="[{'text-right': $store.state.AppActiveUser.role.id === item.author.id}, 'comment__info']" v-if="item.authorFullName || item.commentDatetime">
							<span v-if="item.authorFullName">{{ item.authorFullName }}</span>
							<span
								v-if="item.commentDatetime"
								:title="item.updated_at | dateTime(userDateTimeFormat)"
							>{{ item.authorFullName ? ", " : "" }}{{ item.commentDatetime}}</span>
						</div>
						<div :class="[{'self-comment': item.author && (item.author.id === $store.state.AppActiveUser.role.id)}, 'comment__content']">{{ item.content }}</div>
					</div>
				</li>
			</ul>
		</div>

		<div class="py-4 form-comment list-item__form-comment text-right">
			<div class="flex flex-wrap">
				<div class="flex-1 flex-grow-0 mr-3">
					<vs-avatar
						v-if="$store.state.AppActiveUser.role.avatar"
						class="form-comment__avatar mx-0 mt-0"
						size="large"
						:src="$store.state.AppActiveUser.role.avatar"
					/>
					<vs-avatar v-else class="form-comment__avatar mt-0" size="large" />
				</div>
				<div class="text-black flex-1 text-left">
					<el-input
						type="textarea"
						:autosize="{ minRows: 3 }"
						class="m-0"
						v-validate="'required'"
						name="comment"
						:data-vv-as="$t(`${displayMode === 'post-comments' ? 'PostComment': 'Comment'}`)"
						:placeholder="$t(`${displayMode === 'post-comments' ? 'Placeholder.InputPostComment': 'Placeholder.InputComment'}`)"
						v-model="comment"
					/>
					<span
						class="text-danger text-sm"
						v-show="errors.has('comment')"
					>{{ errors.first('comment') }}</span>
				</div>
			</div>
			<vs-button
				class="mt-4 w-full sm:w-1/5 font-bold"
				@click.stop="sendComment"
			>{{ $t('Send') }}</vs-button>
		</div>
	</div>
</template>

<script>
import _ from "lodash";
import moment from "moment";

export default {
	name: "CbsCommentGeneral",
	inheritAttrs: false,
	props: {
		commentData: {
			type: Object,
			required: true,
			validator: prop => _.has(prop, "data") && _.has(prop, "total") && _.has(prop, "has_more_pages"),
		},
		limit: {
			type: Number,
			default: 5
		},
		displayMode: {
			type: String,
			validator: prop => ["chat", "post-comments"].includes(prop),
			default: "chat"
		}
	},
	emits: {
		sendComment: {
			required: true,
		},
		loadMoreComments: {
			required: true,
		}
	},
	data() {
		return {
			comment: "",

			commentsPage: 1,
			commentsLimit: this.limit
		};
	},
	computed: {
		comments() {
			moment.locale(this.userSettings && this.userSettings.locale === "vn" ? "vi" : "en");
			const comments = _.get(this.commentData, "data") || [];
			return comments.map(comment => ({
				...comment,
				authorFullName: _.get(comment, "author.name") || "",
				commentDatetime: comment.updated_at && moment().diff(comment.updated_at, 'days') >= 1
					? moment(comment.updated_at).fromNow()
					: this.$options.filters.dateTime(comment.updated_at, this.userDateTimeFormat)
			})).reverse();
		}
	},
	methods: {
		sendComment() {
			this.$validator.validateAll().then(async validateionResult => {
				if (validateionResult) {
					try {
						this.$vs.loading();
						const result = await new Promise((resolve, reject) => this.$emit("sendComment", this.comment, resolve, reject));
						if (result) {
							this.$helper.notifySuccess({
								context: this,
								options: {
									text: this.$t(this.displayMode === "post-comments" ? "SendPostCommentSuccessful" : "SendCommentSuccessful")
								}
							});

							this.comment = "";
							this.$validator.reset();
						}
					} catch (error) {
						this.$helper.notifyError({ context: this, error });
					} finally {
						this.$nextTick(() => this.$vs.loading.close());
					}
				}
			});
		},
		async loadMoreComments() {
			const executeTime = performance.now();
			try {
				this.$vs.loading();
				await new Promise((resolve, reject) => this.$emit('loadMoreComments', this.commentsPage += 1, this.commentsLimit, resolve, reject));
			} catch (error) {
				this.commentsPage -= 1;
				this.$helper.notifyError({ context: this, error });
			} finally {
				_.delay(() => this.$vs.loading.close(), performance.now() - executeTime < 200 ? 200 : 0);
			}
		}
	},
};
</script>

<style lang="scss" scoped>
	.cbs-comment-general {
		.list-item__btn-loadmore {
			color: var(--color-solid-primary);
			background: #EBF2FF !important;
			border-radius: 6px;
		}

		.comment__content {
			border-radius: 8px;
			border: 1px solid var(--color-gray-4);
			padding: 12px;
			line-height: 17px;
			white-space: pre-wrap;

			&.self-comment {
				border: none;
				background-color: var(--color-gray-6);
			}
		}
	}
</style>
